import { type CheerioAPI, load as cheerioLoad } from "cheerio";
import { convert as htmlToTextConvert } from "html-to-text";

export function loadHtml(html: string): CheerioAPI {
  return cheerioLoad(html);
}

// Sentinel chars that survive html-to-text's text extraction.
// PARA marks a 2+ consecutive <br> sequence (paragraph break);
// LINE marks a single <br> (joined with a space inside a paragraph).
const PARA = "";
const LINE = "";

export function normalizeText(html: string): string {
  // Convert <br> runs to sentinel markers BEFORE parsing. 2+ consecutive <br>s
  // → paragraph break; single <br> → soft line break joined with a space.
  // html-to-text's default <br> formatter emits \n, which we'd then have to
  // disambiguate from real paragraph breaks; sentinels sidestep that.
  const preprocessed = html
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, PARA)
    .replace(/<br\s*\/?>/gi, LINE);

  const text = htmlToTextConvert(preprocessed, {
    wordwrap: false,
    selectors: [
      { selector: "a", options: { ignoreHref: true } },
      { selector: "img", format: "skip" },
      { selector: "script", format: "skip" },
      { selector: "style", format: "skip" },
      { selector: "h1", options: { uppercase: false } },
      { selector: "h2", options: { uppercase: false } },
      { selector: "h3", options: { uppercase: false } },
      { selector: "h4", options: { uppercase: false } },
      { selector: "h5", options: { uppercase: false } },
      { selector: "h6", options: { uppercase: false } },
    ],
  });

  // Replace sentinels, then split on any run of 2+ newlines (whether from
  // html-to-text's block separators or our PARA marker), collapse intra-block
  // whitespace, and rejoin with a uniform paragraph separator.
  return text
    .replace(new RegExp(LINE, "g"), " ")
    .replace(new RegExp(PARA, "g"), "\n\n")
    .split(/\n{2,}/)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n\n")
    .trim();
}

export function pickText($: CheerioAPI, selectors: readonly string[]): string {
  for (const sel of selectors) {
    const t = $(sel).first().text().trim();
    if (t) return t;
  }
  return "";
}

export function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

interface TurndownInstance {
  turndown(html: string): string;
}

interface TurndownConstructor {
  new (opts?: { headingStyle?: string; bulletListMarker?: string }): TurndownInstance;
}

function isTurndownConstructor(v: unknown): v is TurndownConstructor {
  return typeof v === "function";
}

export async function htmlToMarkdown(html: string): Promise<string> {
  let mod: { default?: unknown };
  try {
    mod = await import("turndown");
  } catch (cause) {
    throw new Error(
      "htmlToMarkdown requires the 'turndown' peer dependency. Install it: pnpm add turndown",
      { cause },
    );
  }
  if (!isTurndownConstructor(mod.default)) {
    throw new Error(
      "htmlToMarkdown: 'turndown' module loaded but its default export is not a constructor; this likely indicates a peer-dep version mismatch.",
    );
  }
  const td = new mod.default({ headingStyle: "atx", bulletListMarker: "-" });
  return td.turndown(html);
}
